import {escapeRegex} from 'common/regex.js';
import http from 'http.js';
import ko from 'knockout';

const API_BASE = '/api/communities/v1/';

export class Community {
    constructor(manager) {
        this.manager = manager;
        this.id = ko.observable();
        this.name = ko.observable();
        this.url = ko.observable();
        this.followed = ko.observable(false);
        this.saving = ko.observable(false);
        // track whether to show save icon, keeping it for a minimum time
        this.showSaving = ko.observable(false);
        this.lastSaveTime = null;

        this.saving.subscribe((value) => {
            const minShowTime = 400; // ms
            // update to show saving as soon as saving starts
            if (value) {
                this.showSaving(true);
                this.lastSaveTime = new Date();
            } else {
                const timeDiff = new Date() - this.lastSaveTime;
                // update immediately if enough time has passed
                if (timeDiff > minShowTime) {
                    this.showSaving(false);
                } else {
                    // set timer that will update this at the appropriate time
                    setTimeout(() => {
                        // only update if saving hasn't re-updated to true
                        if (!this.saving()) {
                            this.showSaving(false);
                        }
                    }, minShowTime - timeDiff);
                }
                this.lastSaveTime = null;
            }
        });
    }

    load(data) {
        this.id(data.id);
        this.name(data.name);
        this.url(data.url);
    }

    // called by follows set, we don't subscribe before so setting init value
    // won't destructively change this data
    activate() {
        this.followed.subscribe((followed) => {
            if (this.skipSave) {
                return;
            }
            this.saving(true);
            if (followed) {
                this.follow();
            } else {
                this.unfollow();
            }
        });
    }

    async follow() {
        const data = {communities: [this.id()]};
        const url = `${API_BASE}follows/`;
        await http.post({url, data});
        this.saving(false);
    }

    async unfollow() {
        const url = `${API_BASE}follows/${this.id()}/`;
        await http.delete({url});
        this.saving(false);
    }

    toString() {
        return `Community: ${this.name()}`;
    }
}

class CommunityGroup {
    constructor(manager) {
        this.manager = manager;
        this.id = ko.observable();
        this.name = ko.observable();
        this.communities = ko.observableArray();
        this.expanded = ko.observable(false);
        this.followed = ko.observable(false);
        // So we can ignotre updates to follows caused by whole group toggle
        this.ignoreUpdates = false;

        this.numFollowed = ko.pureComputed(
            () =>
                this.communities().filter((community) => community.followed())
                    .length,
        );

        this.saving = ko.pureComputed(() => {
            for (const community of this.communities()) {
                if (community.saving()) {
                    return true;
                }
            }
            return false;
        });
    }

    load(data) {
        this.id(data.id);
        this.name(data.name);
        this.communities(
            this.manager
                .communities()
                .filter(
                    (community) =>
                        data.communities.indexOf(community.id()) !== -1,
                ),
        );
    }

    // called once all data is loaded
    // use to trigger initial toggle state
    activate() {
        if (this.numFollowed()) {
            this.expanded(true);
        }
        // Mark us as followed if all our communities are
        this.followed(true);
        for (const community of this.communities()) {
            if (!community.followed()) {
                this.followed(false);
                break;
            }
        }
        // Trigger all communities in this group when this is changed.
        this.followed.subscribe(async (value) => {
            // don't update everything if value is changing from individual
            // changes
            if (this.ignoreUpdates) {
                return;
            }
            this.ignoreUpdates = true;
            // Do bulk saves to avoid unnecessary requests
            if (value) {
                const data = {
                    communities: this.communities().map((c) => c.id()),
                };
                const url = `${API_BASE}follows/`;
                for (const community of this.communities()) {
                    community.saving(true);
                }
                try {
                    await http.post({url, data});
                    for (const community of this.communities()) {
                        community.saving(false);
                        community.skipSave = true;
                        community.followed(true);
                        community.skipSave = false;
                    }
                } catch (ex) {
                    for (const community of this.communities()) {
                        community.saving(false);
                    }
                }
            } else {
                for (const community of this.communities()) {
                    community.followed(false);
                }
            }
            // Flip value as toggle will be called, we want to end up open
            // if we have all ticked, and closed if none ticked
            this.expanded(value);
            this.ignoreUpdates = false;
        });

        for (const community of this.communities()) {
            community.followed.subscribe(this.updateFollowed.bind(this));
        }
    }

    updateFollowed() {
        if (this.ignoreUpdates) {
            return;
        }
        this.ignoreUpdates = true;
        this.followed(this.communities().length === this.numFollowed());
        this.ignoreUpdates = false;
    }

    toggle() {
        this.expanded(!this.expanded());
    }
}

export class CommunitiesManager {
    constructor() {
        this.filter = ko.observable('');
        this.communities = ko.observableArray();
        this.groups = ko.observableArray();

        this.followed = ko.pureComputed(() =>
            this.communities().filter((community) => community.followed()),
        );

        this.filteredCommunities = ko.pureComputed(() => {
            const filter = this.filter().trim();
            if (!filter) {
                return [];
            }
            const regex = new RegExp(`\\b${escapeRegex(filter)}`, 'i');
            return this.communities().filter((community) =>
                regex.test(community.name()),
            );
        });
    }

    async loadCommunities() {
        const url = `${API_BASE}communities/`;
        const data = await http.get({url});
        const coms = [];
        for (const d of data.results) {
            const com = new Community(this);
            com.load(d);
            coms.push(com);
        }
        this.communities(coms);
    }

    async loadGroups(communitiesLoaded) {
        const url = `${API_BASE}groups/`;
        const data = await http.get({url});
        await communitiesLoaded;
        const grs = [];
        for (const d of data.results) {
            const gr = new CommunityGroup(this);
            gr.load(d);
            grs.push(gr);
        }
        this.groups(grs);
    }

    async loadFollows(communitiesLoaded) {
        const url = `${API_BASE}follows/`;
        const data = await http.get({url});
        await communitiesLoaded;
        for (const id of data) {
            this.communities()
                .find((c) => c.id() === id)
                .followed(true);
        }
    }

    async load() {
        const communityLoad = this.loadCommunities();

        await Promise.all([
            communityLoad,
            this.loadGroups(communityLoad),
            this.loadFollows(communityLoad),
        ]);
        for (const community of this.communities()) {
            community.activate();
        }
        // Remove any empty community groups
        this.groups(
            this.groups().filter((group) => group.communities().length),
        );

        for (const group of this.groups()) {
            group.activate();
        }
    }
}
